/**
 * Tree arithmetic shared by folders and teams.
 *
 * Both hierarchies are small enough — an organisation has tens of folders, not
 * millions — that the whole set is loaded and the tree is assembled in memory.
 * That is deliberate: a parent-keyed index would buy nothing and a cycle check
 * needs the whole set anyway, since "is this node below that one" cannot be
 * answered from a single row.
 */
/**
 * Ceiling on nesting depth.
 *
 * Nothing in DynamoDB needs it; the limit exists so the admin screens stay
 * navigable and so a runaway `PATCH` cannot build a chain that every tree walk
 * then has to pay for.
 */
export const MAX_HIERARCHY_DEPTH = 6;
/** A move that would put a node inside its own subtree. */
export class CycleError extends Error {
    constructor(id, parentId) {
        super(`${id} cannot be placed under ${parentId}: that is its own subtree`);
        this.name = "CycleError";
    }
}
function indexById(nodes) {
    return new Map(nodes.map((node) => [node.id, node]));
}
/**
 * Assemble roots and their children.
 *
 * A node whose parent is absent is treated as a root rather than dropped: the
 * parent may have been deleted by another session, and a folder that silently
 * vanishes from the admin screen is worse than one shown at the wrong level.
 */
export function buildForest(nodes) {
    const wrapped = new Map(nodes.map((node) => [node.id, { ...node, children: [] }]));
    const roots = [];
    for (const node of nodes) {
        const wrappedNode = wrapped.get(node.id);
        if (wrappedNode === undefined) {
            continue;
        }
        const parent = node.parentId === null ? undefined : wrapped.get(node.parentId);
        if (parent === undefined) {
            roots.push(wrappedNode);
            continue;
        }
        parent.children.push(wrappedNode);
    }
    sortByName(roots);
    return roots;
}
function sortByName(nodes) {
    nodes.sort((left, right) => left.name.localeCompare(right.name, "ja"));
    for (const node of nodes) {
        sortByName(node.children);
    }
}
/** Every node below `id`, at any depth. */
export function descendantIdsOf(nodes, id) {
    const childrenOf = new Map();
    for (const node of nodes) {
        if (node.parentId === null) {
            continue;
        }
        const siblings = childrenOf.get(node.parentId) ?? [];
        siblings.push(node.id);
        childrenOf.set(node.parentId, siblings);
    }
    const found = new Set();
    const queue = [...(childrenOf.get(id) ?? [])];
    while (queue.length > 0) {
        const current = queue.pop();
        if (found.has(current)) {
            continue;
        }
        found.add(current);
        queue.push(...(childrenOf.get(current) ?? []));
    }
    return found;
}
/**
 * Every node above `id`, nearest first.
 *
 * The chain, not the set: a folder inherits from the folder it sits in before
 * anything further up, and the access rules read the list in that order. Like
 * `depthOf`, it stops at `MAX_HIERARCHY_DEPTH` rather than trusting the stored
 * parents, so a cycle written by a buggy migration cannot hang a request.
 */
export function ancestorIdsOf(nodes, id) {
    const byId = indexById(nodes);
    const ancestors = [];
    let current = byId.get(id);
    while (current !== undefined &&
        current.parentId !== null &&
        ancestors.length < MAX_HIERARCHY_DEPTH) {
        ancestors.push(current.parentId);
        current = byId.get(current.parentId);
    }
    return ancestors;
}
/** 1 for a root. Stops at `MAX_HIERARCHY_DEPTH` so a stored cycle cannot hang. */
export function depthOf(nodes, id) {
    const byId = indexById(nodes);
    let depth = 0;
    let current = byId.get(id);
    while (current !== undefined && depth <= MAX_HIERARCHY_DEPTH) {
        depth += 1;
        current =
            current.parentId === null ? undefined : byId.get(current.parentId);
    }
    return depth;
}
/** Depth of the deepest node in `id`'s subtree, counting `id` itself as 1. */
function subtreeHeightOf(nodes, id) {
    const descendants = descendantIdsOf(nodes, id);
    const base = depthOf(nodes, id);
    let height = 1;
    for (const descendant of descendants) {
        height = Math.max(height, depthOf(nodes, descendant) - base + 1);
    }
    return height;
}
/**
 * Guard a re-parenting before it is written.
 *
 * Three ways it can be wrong, and all three have to be caught here rather than
 * by DynamoDB, which has no opinion about shape: the parent may not exist, the
 * move may form a cycle, and it may push the moved subtree past the depth cap.
 */
export function assertParentIsAssignable(nodes, id, parentId) {
    if (parentId === null) {
        return;
    }
    if (parentId === id) {
        throw new CycleError(id, parentId);
    }
    if (!indexById(nodes).has(parentId)) {
        throw new Error(`parent ${parentId} is not found`);
    }
    if (descendantIdsOf(nodes, id).has(parentId)) {
        throw new CycleError(id, parentId);
    }
    const resulting = depthOf(nodes, parentId) + subtreeHeightOf(nodes, id);
    if (resulting > MAX_HIERARCHY_DEPTH) {
        throw new Error(`the move would nest to depth ${resulting}, over the limit of ${MAX_HIERARCHY_DEPTH}`);
    }
}
/**
 * Depth-first flattening, so a child always follows the parent it is indented
 * under. What the folder and team pickers render: a tree widget buys nothing
 * over an indented option list, and costs the keyboard and screen-reader
 * behaviour a native `<select>` already has.
 */
export function flattenForest(forest, depth = 0) {
    return forest.flatMap((node) => [
        { id: node.id, name: node.name, depth },
        ...flattenForest(node.children, depth + 1),
    ]);
}
//# sourceMappingURL=hierarchy.js.map